import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Avatar, Box, InputAdornment, Skeleton, TextField, Typography } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DomainDisabledOutlined from '@mui/icons-material/DomainDisabledOutlined';
import Search from '@mui/icons-material/Search';
import { GetClientSociete } from "../../services/SocieteService";
import { withSnackbar } from "../../utils/SnackbarHOC";
import { AppButton } from "../../components/UI";
import Modal from "../../components/Modal/Modal";

const companyId = (c) => c?.Uuid ?? c?.uuid;

const normalize = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const initials = (name) =>
  (name || '?').replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

// Same company name → same colour, so a company is easy to recognise in the list.
const palette = [
  ['#ebf4fb', '#03407b'], ['#f0fdf4', '#166534'], ['#fef9c3', '#854d0e'],
  ['#fdf2f8', '#9d174d'], ['#f5f3ff', '#5b21b6'], ['#fff7ed', '#c2410c'],
];
const colorFor = (name) => palette[[...(name || '')].reduce((a, ch) => a + ch.charCodeAt(0), 0) % palette.length];

function ModalMission({ setOpenModal, snackbarShowMessage }) {
  const history = useHistory();
  const [companies, setCompanies] = useState({ isLoading: true, data: [] });
  const [search, setSearch] = useState('');
  const [company, setCompany] = useState(null);

  useEffect(() => {
    let active = true;
    GetClientSociete()
      .then(({ data }) => {
        if (!active) return;
        // The endpoint can return a company several times: keep one entry per company.
        const unique = [...new Map((data || []).map((c) => [companyId(c) ?? c.nom_societe, c])).values()]
          .filter((c) => c?.nom_societe)
          .sort((a, b) => a.nom_societe.localeCompare(b.nom_societe, 'fr', { sensitivity: 'base' }));
        setCompanies({ isLoading: false, data: unique });
      })
      .catch(() => {
        if (!active) return;
        setCompanies({ isLoading: false, data: [] });
        snackbarShowMessage("Erreur de chargement des sociétés.", "error", 4000);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    const q = normalize(search.trim());
    return q ? companies.data.filter((c) => normalize(c.nom_societe).includes(q)) : companies.data;
  }, [companies.data, search]);

  const close = () => setOpenModal(false);

  const goToMissionForm = (target = company) => {
    if (!target) return;
    history.push(`../societe/${companyId(target)}/missions/add`);
  };

  const onSearchKeyDown = (e) => {
    // Enter picks the only match, or confirms the current choice.
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (visible.length === 1) goToMissionForm(visible[0]);
    else if (company) goToMissionForm();
  };

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, borderRadius: '10px', background: '#f0f7fd', border: '1px solid #aed6f1' }}>
        <BusinessCenterOutlined sx={{ color: '#03407b' }} />
        <Typography sx={{ fontSize: '0.9rem', color: '#03407b' }}>
          Choisissez la <b>société cliente</b> : la fiche mission s'ouvrira directement pour elle.
        </Typography>
      </Box>

      <TextField
        autoFocus
        fullWidth
        size="small"
        placeholder="Rechercher une société…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={onSearchKeyDown}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} slotProps={{ input: {
          startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8' }} /></InputAdornment>,
          endAdornment: !companies.isLoading && (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                {visible.length} société{visible.length > 1 ? 's' : ''}
              </Typography>
            </InputAdornment>
          ),
        } }}
      />

      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', height: 320, overflowY: 'auto' }}>
        {companies.isLoading ? (
          [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={44} sx={{ m: 1, borderRadius: '8px' }} />)
        ) : visible.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#94a3b8' }}>
            <DomainDisabledOutlined />
            <Typography sx={{ fontSize: '0.88rem' }}>
              {search ? `Aucune société ne correspond à « ${search} »` : 'Aucune société cliente disponible'}
            </Typography>
          </Box>
        ) : visible.map((c) => {
          const selected = company && companyId(company) === companyId(c);
          const [bg, fg] = colorFor(c.nom_societe);
          return (
            <Box
              key={companyId(c) ?? c.nom_societe}
              role="option"
              aria-selected={!!selected}
              tabIndex={0}
              onClick={() => setCompany(c)}
              onDoubleClick={() => goToMissionForm(c)}
              onKeyDown={(e) => { if (e.key === 'Enter') goToMissionForm(c); else if (e.key === ' ') { e.preventDefault(); setCompany(c); } }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.9, cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9', background: selected ? '#f0f7fd' : '#fff',
                '&:last-of-type': { borderBottom: 'none' },
                '&:hover': { background: selected ? '#e6f1fb' : '#f8fafc' },
                '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: -2 },
              }}
            >
              <Avatar variant="rounded" sx={{ width: 30, height: 30, fontSize: '0.72rem', fontWeight: 700, bgcolor: bg, color: fg, borderRadius: '8px' }}>
                {initials(c.nom_societe)}
              </Avatar>
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.92rem', fontWeight: selected ? 700 : 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.nom_societe}
              </Typography>
              {selected && <CheckCircle sx={{ color: '#03407b', fontSize: 20 }} />}
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: -0.75 }}>
        Astuce : double-cliquez sur une société (ou appuyez sur Entrée) pour continuer directement.
      </Typography>
    </Box>
  );

  const actions = (
    <>
      {company && (
        <Typography sx={{ mr: 'auto', fontSize: '0.85rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50%' }}>
          Société : <b style={{ color: '#03407b' }}>{company.nom_societe}</b>
        </Typography>
      )}
      <AppButton variant="secondary" onClick={close}>
        Annuler
      </AppButton>
      <AppButton variant="primary" onClick={() => goToMissionForm()} disabled={!company} endIcon={<ArrowForward fontSize="small" />}>
        Continuer
      </AppButton>
    </>
  );

  return (
    <Modal open onClose={close} title="Ajouter une mission" maxWidth="sm" actions={actions}>
      {body}
    </Modal>
  );
}

export default withSnackbar(ModalMission);
